import { LABEL_FORMATS, type LabelFormat } from '@epc/contracts';
import { useQueries } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/button';
import { EmptyState, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { itemQuery, qty, toolQuery } from './queries';

/** Размеры этикеток, мм (СК-10): лист A4 на 8, термопринтер 100×70 и 58×40 */
const SIZES: Record<LabelFormat, { w: number; h: number; perPage: number | null; qr: number }> = {
  a4_8: { w: 99, h: 67, perPage: 8, qr: 40 },
  t100x70: { w: 100, h: 70, perPage: null, qr: 42 },
  t58x40: { w: 58, h: 40, perPage: null, qr: 26 },
};

interface LabelData {
  key: string;
  id: string;
  code: string;
  name: string;
  unit: string;
  conv: string | null;
  url: string;
  qr: string;
}

function labelHtml(labels: LabelData[], format: LabelFormat, doc: string | null): string {
  const s = SIZES[format];
  const esc = (x: string) => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cell = (l: LabelData) => `
    <div class="label">
      <img src="${l.qr}" alt="" />
      <div class="t">
        <div class="code">${esc(l.code)}</div>
        <div class="name">${esc(l.name)}</div>
        <div class="unit">${esc(l.unit)}${l.conv ? ` · ${esc(l.conv)}` : ''}${doc ? ` · ${esc(doc)}` : ''}</div>
      </div>
    </div>`;
  const page =
    format === 'a4_8'
      ? '@page { size: A4; margin: 10mm 6mm; }'
      : `@page { size: ${s.w}mm ${s.h}mm; margin: 0; }`;
  const grid =
    format === 'a4_8'
      ? `.sheet { display: grid; grid-template-columns: repeat(2, ${s.w}mm); grid-auto-rows: ${s.h}mm; gap: 2mm 0; }`
      : '.sheet { display: block; } .label { page-break-after: always; }';
  return `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>Этикетки</title>
<style>
  ${page}
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
  ${grid}
  .label { box-sizing: border-box; width: ${s.w}mm; height: ${s.h}mm; padding: 3mm; display: flex; gap: 3mm; align-items: center; border: 0.2mm dashed #999; overflow: hidden; }
  @media print { .label { border-color: transparent; } }
  img { width: ${s.qr}mm; height: ${s.qr}mm; flex: none; }
  .t { min-width: 0; display: flex; flex-direction: column; gap: 1mm; }
  .code { font-size: ${format === 't58x40' ? 10 : 13}pt; font-weight: 700; letter-spacing: 0.02em; }
  .name { font-size: ${format === 't58x40' ? 8 : 11}pt; line-height: 1.2; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .unit { font-size: ${format === 't58x40' ? 7 : 9}pt; color: #333; }
</style></head><body><div class="sheet">${labels.map(cell).join('')}</div></body></html>`;
}

/** Этикетки с QR на упаковку и позицию (СК-10): скан открывает карточку позиции */
export function LabelsPage() {
  const search = useSearch({ strict: false }) as {
    items?: string;
    units?: string;
    doc?: string;
    format?: string;
  };
  useCrumbs([{ label: 'Этикетки' }]);
  const ids = useMemo(() => (search.items ?? '').split(',').filter(Boolean).slice(0, 200), [search.items]);
  const unitIds = useMemo(
    () => (search.units ?? '').split(',').filter(Boolean).slice(0, 200),
    [search.units],
  );
  const [format, setFormat] = useState<LabelFormat>(
    (search.format as LabelFormat) in SIZES ? (search.format as LabelFormat) : 'a4_8',
  );
  const [copies, setCopies] = useState('1');
  const items = useQueries({ queries: ids.map((id) => itemQuery(id)) });
  const units = useQueries({ queries: unitIds.map((id) => toolQuery(id)) });
  const loaded = items.every((q) => q.isSuccess) && units.every((q) => q.isSuccess);
  const [qrs, setQrs] = useState<Record<string, string>>({});
  // Что кодирует QR: позиция каталога или экземпляр инструмента (СК-10: скан открывает карточку)
  const targets = useMemo(
    () => [
      ...ids.map((id) => ({ id, url: `/warehouse/items/${id}` })),
      ...unitIds.map((id) => ({ id, url: `/warehouse/tools/${id}` })),
    ],
    [ids, unitIds],
  );
  useEffect(() => {
    let alive = true;
    (async () => {
      const out: Record<string, string> = {};
      for (const t of targets) {
        out[t.id] = await QRCode.toDataURL(`${window.location.origin}${t.url}`, {
          margin: 0,
          width: 240,
          errorCorrectionLevel: 'M',
        });
      }
      if (alive) setQrs(out);
    })();
    return () => {
      alive = false;
    };
  }, [targets]);
  const n = Math.min(Math.max(Number(copies) || 1, 1), 50);
  const labels: LabelData[] = loaded
    ? [
        ...items.flatMap((q) => {
          const it = q.data!;
          return Array.from({ length: n }, (_, copy) => ({
            key: `${it.id}-${copy}`,
            id: it.id,
            code: it.code,
            name: it.name,
            unit: it.unit,
            conv: it.convUnit ? `1 ${it.unit} = ${qty(it.convFactor)} ${it.convUnit}` : null,
            url: `/warehouse/items/${it.id}`,
            qr: qrs[it.id] ?? '',
          }));
        }),
        ...units.flatMap((q) => {
          const u = q.data!;
          return Array.from({ length: n }, (_, copy) => ({
            key: `${u.id}-${copy}`,
            id: u.id,
            code: u.inventoryNumber,
            name: `${u.itemName}${u.model ? ` · ${u.model}` : ''}`,
            unit: u.serialNumber ? `s/n ${u.serialNumber}` : u.itemCode,
            conv: null,
            url: `/warehouse/tools/${u.id}`,
            qr: qrs[u.id] ?? '',
          }));
        }),
      ]
    : [];
  const ready = loaded && labels.every((l) => l.qr);
  const print = () => {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      toast('Браузер не дал открыть окно печати — разрешите всплывающие окна для портала', 'danger');
      return;
    }
    win.document.write(labelHtml(labels, format, search.doc ?? null));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };
  const s = SIZES[format];
  return (
    <Page>
      <PageHeader
        title="Этикетки с QR"
        subtitle="QR открывает карточку позиции или экземпляра инструмента в портале; лист A4 на 8 этикеток или термопринтер 100×70 и 58×40 мм (СК-10)"
        actions={
          <Button
            variant="primary"
            icon={<Printer className="size-4" />}
            disabled={!ready || !labels.length}
            onClick={print}
          >
            Печать
          </Button>
        }
      />
      {!ids.length && !unitIds.length ? (
        <EmptyState title="Нечего печатать">
          Отметьте позиции в каталоге или экземпляры инструмента, или откройте этикетки из поступления.
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 grid max-w-xl gap-4 sm:grid-cols-2">
            <Field label="Формат">
              <Select value={format} onChange={(e) => setFormat(e.target.value as LabelFormat)}>
                {(Object.keys(LABEL_FORMATS) as LabelFormat[]).map((k) => (
                  <option key={k} value={k}>
                    {LABEL_FORMATS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Копий на позицию">
              <Input inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value)} />
            </Field>
          </div>
          <Panel title="Предпросмотр" meta={`${labels.length} шт., ${s.w}×${s.h} мм`}>
            {!ready ? (
              <Spinner label="Готовим QR" />
            ) : (
              <div className="flex flex-wrap gap-3">
                {labels.map((l) => (
                  <div
                    key={l.key}
                    className="flex items-center gap-3 rounded-sm border border-dashed border-line-strong bg-white p-3 text-black"
                    style={{ width: `${s.w}mm`, height: `${s.h}mm`, maxWidth: '100%' }}
                  >
                    <img src={l.qr} alt="" style={{ width: `${s.qr}mm`, height: `${s.qr}mm` }} />
                    <div className="min-w-0">
                      <div className="num text-[13px] font-bold">{l.code}</div>
                      <div className="line-clamp-3 text-[11px] leading-tight">{l.name}</div>
                      <div className="text-[9px] text-neutral-600">
                        {l.unit}
                        {l.conv ? ` · ${l.conv}` : ''}
                        {search.doc ? ` · ${search.doc}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}
    </Page>
  );
}
